import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'

import router from '@/router'
import { menuRouteEnum, menuRoutes } from '@/lib/menu-routes'
import { getI18n, paramsSubstituteTranslate } from '@/plugins/i18n'
import { formatDayMonthDate, formatShortDate, formatShortDateTime } from '@/lib/utils'

import { webSiteApi } from '@/apis/web-site.api'
import { notificationAttentionTypes, notificationTargetEnum, usePortalStore } from '@/stores/portal.store'
import { searchResultTypeEnum, useWebSiteStore } from '@/stores/web-site.store'
import { useODataSource, useODataStore } from '@/composables/data-grid'
import { replaceTimeInISO } from '@/composables/date-time'
import { navigateToBudgetDocument, navigateToInvoice, navigateToProject, navigateToWarehouseMovements } from '@/composables/navigator'

dayjs.extend(isoWeek)

const DATE_PARAMS = Object.freeze(['day_start', 'day_end', 'days', 'due_date'])
const DATETIME_PARAMS = Object.freeze(['start_at', 'end_at'])
const NEWEST_FIRST = Object.freeze([{ selector: 'visible_from', desc: true }])
const DEFAULT_SNOOZE_TIME = '09:00'

const TARGETS = Object.freeze({
  [notificationTargetEnum.TIMELINE]: { route: menuRouteEnum.TIMELINE, icon: 'mdi-chart-timeline', prefixKey: 'menu.timeline' },
  [notificationTargetEnum.PROJECT]: { route: menuRouteEnum.PROJECTS, icon: 'mdi-briefcase-outline' },
  [notificationTargetEnum.QUOTE]: { route: menuRouteEnum.ACCOUNTING_BUDGET_DOCUMENTS, icon: 'mdi-file-document-outline' },
  [notificationTargetEnum.INVOICES]: { route: menuRouteEnum.ACCOUNTING_INVOICES, icon: 'mdi-file-document-check-outline', prefixKey: 'common.invoice' },
  [notificationTargetEnum.WAREHOUSE_MOVEMENTS]: { route: menuRouteEnum.WAREHOUSE_LIST, icon: 'mdi-dolly' },
  [notificationTargetEnum.STAFF_ABSENCES]: { route: menuRouteEnum.STAFF_MOVEMENTS, icon: 'mdi-calendar-account' },
  [notificationTargetEnum.WAREHOUSE_AVAILABILITY]: { route: menuRouteEnum.WAREHOUSE_STOCK, icon: 'mdi-chart-gantt' },
  [notificationTargetEnum.EQUIPMENT]: { route: menuRouteEnum.EQUIPMENTS, icon: 'mdi-camera-outline' },
  [notificationTargetEnum.TASKS]: { route: null, icon: 'mdi-checkbox-marked-circle-outline' },
})

export const snoozePresets = () => {
  const i18n = getI18n()
  const time = useWebSiteStore().getDefaultStartTime() || DEFAULT_SNOOZE_TIME
  const atWorkStart = (day) => dayjs(replaceTimeInISO(day, time))
  return [
    { label: i18n.t('common.snoozeOneHour'), until: () => dayjs().add(1, 'hour') },
    { label: i18n.t('common.snoozeThreeHours'), until: () => dayjs().add(3, 'hour') },
    { label: i18n.t('common.snoozeTomorrow', { time }), until: () => atWorkStart(dayjs().add(1, 'day')) },
    { label: i18n.t('common.snoozeNextMonday', { time }), until: () => atWorkStart(dayjs().add(1, 'week').startOf('isoWeek')) },
  ]
}

const formatParams = (params, keys, format) => Object.fromEntries(keys.filter(key => params?.[key]).map(key => [key, format(params[key])]))

export const useNotifications = () => {
  const i18n = getI18n()
  const portalStore = usePortalStore()
  const webSiteStore = useWebSiteStore()

  const notificationsUrl = webSiteApi.getAbsoluteUrl() + webSiteApi.notificationsUrl
  const unreadOnlyParams = (unreadOnly) => () => (unreadOnly() ? { is_read: false } : null)

  const notificationTypeName = ({ type }) => portalStore.getLocalizedNotificationTypes().find(item => item.id === type)?.name

  const notificationTypeHint = ({ type }) => {
    const name = Object.keys(portalStore.notificationTypeEnum).find(key => portalStore.notificationTypeEnum[key] === type)
    return name ? i18n.t(`common.notificationTypeHints.${name.toLowerCase()}`) : null
  }

  const notificationTypeChips = (notification) => {
    const category = portalStore.getLocalizedNotificationCategories().find(item => item.id === notification.category)
    if (!category) return []
    return [{ id: notification.type, name: notificationTypeName(notification), color: category.color, textColor: category.textColor }]
  }

  const isAttentionNotification = ({ type }) => notificationAttentionTypes.includes(type)

  const hasTarget = ({ target_type }) => !!TARGETS[target_type]

  const notificationTargetIcon = ({ target_type }) => TARGETS[target_type]?.icon

  const notificationTargetDetail = ({ target_type, params }) => (target_type === notificationTargetEnum.TIMELINE
    ? formatShortDate(params?.target_date)
    : params?.project_name ?? params?.quote_name ?? params?.invoice_number ?? params?.equipment_name ?? params?.service_name ?? params?.task_title)

  const notificationTargetLabel = (notification) => {
    const { prefixKey, route } = TARGETS[notification.target_type]
    const detail = notificationTargetDetail(notification)
    if (!detail) return i18n.t(route)
    return prefixKey ? `${i18n.t(prefixKey, 1)} ${detail}` : detail
  }

  const notificationMessage = ({ message, params }) => paramsSubstituteTranslate(
    message,
    { ...params, ...formatParams(params, DATE_PARAMS, formatShortDate), ...formatParams(params, DATETIME_PARAMS, formatShortDateTime) },
    params?.count ?? null,
  )

  const notificationDay = ({ visible_from }) => (dayjs(visible_from).isSame(dayjs(), 'day') ? i18n.t('common.today') : formatDayMonthDate(visible_from))

  const markAsReadAsync = (ids) => webSiteStore.setNotificationsReadAsync({ ids })

  const markAsUnreadAsync = (ids) => webSiteStore.setNotificationsReadAsync({ ids, is_read: false })

  const markAllAsReadAsync = () => webSiteStore.setNotificationsReadAsync({ all: true })

  const snoozeAsync = (ids, until) => webSiteStore.snoozeNotificationsAsync(ids, dayjs(until).toISOString())

  const toggleReadAsync = async(notification) => {
    await (notification.is_read ? markAsUnreadAsync : markAsReadAsync)([notification.id])
    notification.is_read = !notification.is_read
  }

  const navigateToTarget = ({ target_type, target_id, params }) => {
    if (target_type === notificationTargetEnum.PROJECT) return navigateToProject(target_id)
    if (target_type === notificationTargetEnum.QUOTE) return navigateToBudgetDocument(params)
    const invoiceNumbers = params?.invoice_numbers ?? [params?.invoice_number].filter(Boolean)
    if (target_type === notificationTargetEnum.INVOICES && invoiceNumbers.length) return navigateToInvoice(invoiceNumbers)
    if (target_type === notificationTargetEnum.WAREHOUSE_MOVEMENTS) return navigateToWarehouseMovements({ warehouse: target_id })
    if (target_type === notificationTargetEnum.WAREHOUSE_AVAILABILITY) {
      return router.push({ path: menuRoutes[menuRouteEnum.WAREHOUSE_STOCK], query: { serviceId: target_id, date: params?.day_start } })
    }
    if (target_type === notificationTargetEnum.EQUIPMENT) return webSiteStore.openSearchResultAsync({ type: searchResultTypeEnum.EQUIPMENT, id: target_id })
    if (target_type === notificationTargetEnum.TASKS) {
      webSiteStore.showTasks = true
      return
    }
    if (target_type === notificationTargetEnum.STAFF_ABSENCES) webSiteStore.staffMovementsOnlyAbsences = true
    if (target_type === notificationTargetEnum.TIMELINE) {
      webSiteStore.timelineFocusDate = params?.target_date ?? null
      return navigateToProject(target_id ?? null, menuRouteEnum.TIMELINE)
    }
    return router.push(menuRoutes[TARGETS[target_type].route])
  }

  const detailTypes = {
    [notificationTargetEnum.PROJECT]: searchResultTypeEnum.PROJECT,
    [notificationTargetEnum.QUOTE]: searchResultTypeEnum.QUOTE,
    [notificationTargetEnum.EQUIPMENT]: searchResultTypeEnum.EQUIPMENT,
  }
  const notificationDetail = ({ target_type, target_id }) => (detailTypes[target_type] && target_id ? { type: detailTypes[target_type], id: target_id } : null)

  const openNotificationAsync = async(notification, { openDetail = false } = {}) => {
    const wasRead = notification.is_read
    notification.is_read = true
    const detail = openDetail && notificationDetail(notification)
    await Promise.all([
      wasRead || markAsReadAsync([notification.id]),
      detail ? webSiteStore.openSearchResultDetailAsync(detail) : hasTarget(notification) && navigateToTarget(notification),
    ])
  }

  const useNotificationsDataSource = (options = {}) => useODataSource(notificationsUrl, {
    ...options,
    customParamsFn: unreadOnlyParams(() => webSiteStore.notificationsUnreadOnly),
  })

  const loadLatestNotificationsAsync = ({ take, unreadOnly }) => useODataStore(notificationsUrl, {
    customParamsFn: unreadOnlyParams(() => unreadOnly),
  }).load({ take, sort: NEWEST_FIRST })

  return {
    notificationTypeName,
    notificationTypeHint,
    notificationTypeChips,
    isAttentionNotification,
    hasTarget,
    notificationTargetIcon,
    notificationTargetLabel,
    notificationMessage,
    notificationDay,
    markAsReadAsync,
    markAsUnreadAsync,
    markAllAsReadAsync,
    snoozeAsync,
    toggleReadAsync,
    openNotificationAsync,
    useNotificationsDataSource,
    loadLatestNotificationsAsync,
  }
}
